import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { convertHeicFilesToJpeg, filterOutRawFiles } from '../lib/heicConvert';
import { detectInAppBrowser } from '../lib/inAppBrowser';
import { uploadToSignedUrl, getPhotoUploadTokens, confirmUploadedPhotos, runWithConcurrency, UPLOAD_CONCURRENCY } from '../lib/storageUpload';
import './ProductDetail.css';
import './OrderForm.css';
import './UploadProduct.css';

// Compress image using Canvas API - reduces memory usage on mobile
const compressImageFile = (file, maxDimension = 2000, quality = 0.88) => {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.src = url;
    // img.decode() guarantees the image is fully decoded before drawing -
    // see OrderForm.js's compressImageFile for why this matters (onload
    // alone let some images get drawn undecoded, exporting as solid black).
    img.decode().then(() => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (width > maxDimension || height > maxDimension) {
        if (width > height) {
          height = Math.round((height / width) * maxDimension);
          width = maxDimension;
        } else {
          width = Math.round((width / height) * maxDimension);
          height = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);
      img.src = '';
      canvas.toBlob(
        (blob) => {
          canvas.width = 0;
          canvas.height = 0;
          if (!blob) { resolve(file); return; }
          const compressed = new File([blob], file.name.replace(/\.[^.]+$/, '.jpg'), { type: 'image/jpeg' });
          resolve(compressed);
        },
        'image/jpeg',
        quality
      );
    }).catch(() => { URL.revokeObjectURL(url); resolve(file); });
  });
};

// Fetch with timeout - extended for large image uploads (100+ photos)
const fetchWithTimeout = async (url, options, timeout = 600000) => {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);
  try {
    const response = await fetch(url, { ...options, signal: controller.signal });
    clearTimeout(timeoutId);
    return response;
  } catch (error) {
    clearTimeout(timeoutId);
    throw error;
  }
};

// Safari reports fetch network failures as "TypeError: Load failed" (no
// "fetch" substring), while Chrome says "Failed to fetch" - both must be
// treated as retryable, or every blip forces the customer to redo the upload.
const isRetryableNetworkError = (err) =>
  err.name === "AbortError" ||
  (err.name === "TypeError" && /fetch|load failed|network/i.test(err.message || ""));

// /finalize only writes order.json - an idempotent overwrite keyed by
// orderId - so retrying it is safe, unlike /init which would create a
// duplicate Supabase order row on retry.
const postJsonWithRetry = async (url, body, timeout, maxAttempts = 2) => {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      const response = await fetchWithTimeout(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      }, timeout);
      if (!response.ok) {
        const errorText = await response.text().catch(() => "");
        const err = new Error(`Server error ${response.status}: ${errorText}`);
        err.status = response.status;
        throw err;
      }
      return response;
    } catch (err) {
      lastErr = err;
      const retryable = isRetryableNetworkError(err) || (err.status && err.status >= 500);
      if (!retryable || attempt === maxAttempts) throw err;
      await new Promise(resolve => setTimeout(resolve, attempt * 1000));
    }
  }
  throw lastErr;
};

const BATCH_UPLOAD_SIZE = 12;
const chunkArray = (arr, size) => {
  const chunks = [];
  for (let i = 0; i < arr.length; i += size) chunks.push(arr.slice(i, i + size));
  return chunks;
};

const UploadProduct = () => {
  const navigate = useNavigate();

  const [selectedOption, setSelectedOption] = useState({
    price: 24.90,
    label: "50 deri 75 Foto",
    minPhotos: 1,
    maxPhotos: 75
  });
  const [uploadedImages, setUploadedImages] = useState([]);

  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [totalPhotos, setTotalPhotos] = useState(0);
  const [orderSubmitted, setOrderSubmitted] = useState(false);
  const [orderPhotoUrl, setOrderPhotoUrl] = useState(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [showNameModal, setShowNameModal] = useState(false);
  const [clientName, setClientName] = useState('');
  const [failedPhotos, setFailedPhotos] = useState([]);
  const [inAppBrowser] = useState(() => detectInAppBrowser());

  // Synchronous lock (state updates are async, so a fast double-tap on
  // "Vazhdo" - very common when the modal doesn't visibly close instantly on
  // slower phones - could otherwise start two overlapping upload sessions
  // that stomp on the same React state and even create two separate orders.
  const submittingRef = useRef(false);

  // Prevent user from accidentally closing the page during upload
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (uploading) {
        e.preventDefault();
        e.returnValue = 'Ngarkimi i fotove është në progres. A jeni të sigurt që doni të dilni?';
        return e.returnValue;
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [uploading]);

  const priceOptions = [
    { price: 24.90, label: "50 deri 75 Foto", minPhotos: 1, maxPhotos: 75 },
    { price: 34.90, label: "75 deri 150 Foto", minPhotos: 1, maxPhotos: 150 },
    { price: 55.00, label: "Deri në 220 Foto", minPhotos: 1, maxPhotos: 220 }
  ];

  // Special hidden product
  const uploadProduct = {
    id: 'upload-special',
    name: 'Ngarkimi Manual',
    fullDescription: 'Perzgjedhni fotot tuaja, dhe ngarkoni ato me poshte. Fotot renditen sipas selektimit që ju bëni. Zgjidhni opsionin më të përshtatshëm për numrin e fotove që dëshironi të përfshini në albumin tuaj.',
    features: [
      'Fotot renditen sipas selektimit tuaj',
      'Opsione fleksibile për numrin e fotove',
      'Cilësi e lartë printimi',
      'Personalizim i plotë'
    ]
  };

  const handleImageUpload = async (e) => {
    const files = filterOutRawFiles(Array.from(e.target.files));
    if (files.length === 0) return;
    const currentCount = uploadedImages.length;
    const newCount = currentCount + files.length;
    const maxAllowed = selectedOption.maxPhotos;

    if (newCount > maxAllowed) {
      const remaining = maxAllowed - currentCount;
      if (remaining <= 0) {
        alert(`Keni arritur limitin maksimal prej ${maxAllowed} fotosh për pakën e zgjedhur (${selectedOption.label}).\n\nNëse dëshironi të ngarkoni më shumë foto, zgjidhni pakën tjetër.`);
        return;
      } else {
        alert(`Mund të ngarkoni vetëm ${remaining} foto të tjera.\n\nKeni zgjedhur pakën: ${selectedOption.label} (max ${maxAllowed} foto)`);
        const allowedFiles = await convertHeicFilesToJpeg(files.slice(0, remaining));
        const imageUrls = allowedFiles.map(file => ({
          url: URL.createObjectURL(file),
          file: file,
          name: file.name
        }));
        setUploadedImages([...uploadedImages, ...imageUrls]);
        return;
      }
    }

    const convertedFiles = await convertHeicFilesToJpeg(files);
    const imageUrls = convertedFiles.map(file => ({
      url: URL.createObjectURL(file),
      file: file,
      name: file.name
    }));
    setUploadedImages([...uploadedImages, ...imageUrls]);
  };

  const removeImage = (index) => {
    const newImages = uploadedImages.filter((_, i) => i !== index);
    setUploadedImages(newImages);
  };

  const handleCopyPhotoLink = async () => {
    if (!orderPhotoUrl) return;
    try {
      await navigator.clipboard.writeText(orderPhotoUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy photo link:', err);
    }
  };

  const handleGenerateLink = async (name) => {
    if (submittingRef.current) return;
    if (uploadedImages.length < selectedOption.minPhotos) {
      alert(`Ju lutem ngarkoni të paktën ${selectedOption.minPhotos} foto për këtë opsion.`);
      return;
    }

    submittingRef.current = true;
    setUploading(true);
    setUploadProgress(0);
    setCurrentPhotoIndex(0);
    setTotalPhotos(uploadedImages.length);
    setFailedPhotos([]);

    try {
      const hostname = window.location.hostname.toLowerCase();
      const isLocalhost =
        hostname === "localhost" || hostname === "127.0.0.1" || hostname === "" || hostname.includes("localhost");
      const API_URL = isLocalhost ? "http://localhost:5000" : "https://orders.fletaime.com";

      // Step 1: /init - creates the order, no photos yet. Single attempt (not
      // retried) since retrying it would create a duplicate Supabase order row.
      setUploadProgress(5);
      const initResponse = await fetchWithTimeout(`${API_URL}/upload-order/init`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          emri: name,
          emriProduktit: uploadProduct.name,
          cmimi: selectedOption.price,
          faqet: selectedOption.label,
          numriFotove: uploadedImages.length,
          orderSource: "uploadproduct",
        }),
      }, 30000);

      if (!initResponse.ok) {
        const errorText = await initResponse.text().catch(() => "");
        throw new Error(`Gabim gjatë fillimit të porosisë (Status: ${initResponse.status}). ${errorText}`);
      }
      const initData = await initResponse.json();
      if (!initData.success) {
        throw new Error("Gabim gjatë fillimit të porosisë!");
      }
      const { orderId, supabaseOrderId } = initData;

      setUploadProgress(10);

      const originalFiles = uploadedImages.filter(img => img.file).map(img => img.file);

      setUploadProgress(15);

      // Step 2: process (HEIC-convert + compress) and upload one small batch
      // at a time, instead of compressing every photo upfront. Holding 100+
      // compressed blobs in memory at once (on top of the originals) is what
      // was crashing mobile Safari on the 150-photo package before the
      // upload request was even sent - see production_incident memory. Each
      // batch's compressed data can be garbage-collected once its request
      // completes, keeping peak memory bounded to ~one batch regardless of
      // how many photos the customer selected.
      const photoNames = [];
      const orderFailedPhotos = [];
      const chunks = chunkArray(originalFiles, BATCH_UPLOAD_SIZE);
      let uploadedSoFar = 0;

      for (let c = 0; c < chunks.length; c++) {
        const chunkOriginals = chunks[c];
        const heicConverted = await convertHeicFilesToJpeg(chunkOriginals);

        const compressedChunk = [];
        for (let i = 0; i < heicConverted.length; i++) {
          compressedChunk.push(await compressImageFile(heicConverted[i]));
          await new Promise(resolve => setTimeout(resolve, 30));
        }

        const startIndex = uploadedSoFar;
        // Each photo uploads directly to Supabase Storage (not through this
        // server) - see order_upload_storage_migration memory. Naming
        // mirrors the old server-generated pattern so downstream code (PDF
        // pipeline, admin dashboard) sees familiar-looking filenames.
        const fileNames = compressedChunk.map((_, i) => `${supabaseOrderId}_${Date.now()}_${startIndex + i}.jpg`);

        try {
          const tokens = await getPhotoUploadTokens(API_URL, orderId, fileNames);
          const tokenByFileName = new Map(tokens.map(t => [t.fileName, t]));
          // Collected as {path, i} so the confirmed order matches the
          // customer's original photo order even with concurrent uploads.
          const uploadedPathEntries = [];

          await runWithConcurrency(compressedChunk, UPLOAD_CONCURRENCY, async (file, i) => {
            const fileName = fileNames[i];
            const tokenInfo = tokenByFileName.get(fileName);
            if (!tokenInfo) {
              orderFailedPhotos.push({ originalName: chunkOriginals[i]?.name || fileName, reason: "token_deshtoi" });
              return;
            }
            try {
              await uploadToSignedUrl(tokenInfo.signedUrl, file);
              uploadedPathEntries.push({ path: tokenInfo.path, i });
            } catch (fileErr) {
              // A single photo that fails even after retries used to abort
              // the WHOLE order, forcing the customer to restart from zero
              // even if 140+/150 photos had already gone through. Skip just
              // this one and keep going - see order_upload_resilience memory.
              console.error(`Photo ${startIndex + i + 1} failed after retries:`, fileErr);
              orderFailedPhotos.push({ originalName: chunkOriginals[i]?.name || fileName, reason: "lidhje_e_paqendrueshme" });
            }
          });

          if (uploadedPathEntries.length > 0) {
            uploadedPathEntries.sort((a, b) => a.i - b.i);
            const urls = await confirmUploadedPhotos(
              API_URL, orderId, supabaseOrderId, uploadedPathEntries.map(e => e.path)
            );
            photoNames.push(...urls);
          }
        } catch (batchErr) {
          console.error(`Batch ${c + 1}/${chunks.length} failed:`, batchErr);
          chunkOriginals.forEach(f => orderFailedPhotos.push({ originalName: f.name, reason: "lidhje_e_paqendrueshme" }));
        }

        uploadedSoFar += chunkOriginals.length;
        setCurrentPhotoIndex(uploadedSoFar);
        // Progress 15% to 90% across all batches (compress + upload combined)
        const progress = 15 + (uploadedSoFar / Math.max(originalFiles.length, 1)) * 75;
        setUploadProgress(Math.round(progress));

        // Yield so this batch's compressed blobs can be GC'd before the next
        await new Promise(resolve => setTimeout(resolve, 50));
      }

      if (photoNames.length === 0) {
        throw new Error(
          "Asnjë foto nuk u ngarkua me sukses. Ju lutem provoni përsëri me lidhje interneti më stabile " +
          "(ose hapeni këtë faqe në Safari/Chrome në vend të Instagram/Facebook)."
        );
      }

      setFailedPhotos(orderFailedPhotos);

      // Step 3: /finalize - writes order.json. Safe to retry (idempotent overwrite).
      setUploadProgress(92);
      setCurrentPhotoIndex(uploadedImages.length);

      const finalizeResponse = await postJsonWithRetry(
        `${API_URL}/upload-order/finalize`,
        { orderId, supabaseOrderId, photoNames },
        120000,
        2
      );

      setUploadProgress(99);

      const data = await finalizeResponse.json();
      if (!data.success) {
        alert("Gabim gjatë ngarkimit të fotove!");
        setUploading(false);
        setUploadProgress(0);
        return;
      }

      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));

      setOrderPhotoUrl(data.orderUrl);
      setOrderSubmitted(true);
      setUploading(false);
      setUploadProgress(0);
    } catch (err) {
      console.error("Error generating photo link:", err);

      let errorMessage = "Gabim gjatë ngarkimit të fotove!";
      if (err.name === "AbortError") {
        errorMessage = "Ngarkimi i fotove po merr shumë kohë. Provo përsëri me më pak foto ose foto më të vogla.";
      } else if (err.name === "TypeError" && err.message.includes("fetch")) {
        errorMessage = "Nuk mund të lidhem me serverin. Kontrollo lidhjen tënde me internetin.";
      } else if (err.message) {
        errorMessage = err.message;
      }

      alert(errorMessage);
      setUploading(false);
      setUploadProgress(0);
    } finally {
      submittingRef.current = false;
    }
  };

  return (
    <div className="product-detail-page">
      {/* Upload Progress Preloader */}
      {uploading && (
        <div className="upload-preloader-overlay">
          <div className="upload-preloader-content">
            <div className="book-animation-container">
              <div className="book">
                <div className="book__pg-shadow"></div>
                <div className="book__pg"></div>
                <div className="book__pg book__pg--2"></div>
                <div className="book__pg book__pg--3"></div>
                <div className="book__pg book__pg--4"></div>
                <div className="book__pg book__pg--5"></div>
              </div>
              <div className="progress-percentage">{Math.round(uploadProgress)}%</div>
            </div>
            <h2 className="upload-message-warning">Qendroni ketu me ne</h2>
            <p className="upload-message-warning">
              Disa minuta sa të ngarkohen të gjitha fotot.
            </p>
            <div className="upload-photo-count">
              <span className="photo-count-number">{currentPhotoIndex} / {totalPhotos}</span>
              <span className="photo-count-label">foto të ngarkuara</span>
            </div>
          </div>
        </div>
      )}

      {/* Modal - Emri i Klientit (kërkohet para gjenerimit të linkut) */}
      {showNameModal && (
        <div className="manual-success-overlay">
          <div className="manual-success-modal">
            <h3>Emri i Klientit</h3>
            <p>Ju lutem shkruani emrin e klientit para se të gjenerohet linku i fotove.</p>

            <input
              type="text"
              value={clientName}
              onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  if (!clientName.trim()) {
                    alert('Ju lutem shkruani emrin e klientit.');
                    return;
                  }
                  setShowNameModal(false);
                  handleGenerateLink(clientName.trim());
                }
              }}
              placeholder="p.sh. Agim Krasniqi"
              autoFocus
              style={{
                width: '100%',
                padding: '12px',
                fontSize: '16px',
                marginBottom: '16px',
                borderRadius: '8px',
                border: '1px solid #ccc',
                boxSizing: 'border-box'
              }}
            />

            <div className="manual-success-actions">
              <button
                type="button"
                onClick={() => {
                  if (!clientName.trim()) {
                    alert('Ju lutem shkruani emrin e klientit.');
                    return;
                  }
                  setShowNameModal(false);
                  handleGenerateLink(clientName.trim());
                }}
                className="whatsapp-fallback-btn"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                Vazhdo
              </button>
            </div>

            <button
              onClick={() => setShowNameModal(false)}
              className="back-home-btn"
            >
              Anulo
            </button>
          </div>
        </div>
      )}

      {/* Success Popup - Linku i Fotove */}
      {orderSubmitted && (
        <div className="manual-success-overlay">
          <div className="manual-success-modal">
            <div className="success-icon">✅</div>
            <h3>Linku u Gjenerua me Sukses!</h3>
            <p>
              {failedPhotos.length > 0
                ? `${totalPhotos - failedPhotos.length} nga ${totalPhotos} foto u ngarkuan me sukses.`
                : "Fotot tuaja u ngarkuan me sukses."}
            </p>

            <div className="images-reminder">
              <p className="reminder-text">
                📸 Linku i fotove tuaja është gati. Mund ta shikoni ose ta kopjoni për ta ruajtur.
              </p>
            </div>

            {failedPhotos.length > 0 && (
              <div className="images-reminder" style={{ background: '#fff3cd', border: '1px solid #ffe69c' }}>
                <p className="reminder-text" style={{ color: '#856404' }}>
                  ⚠️ {failedPhotos.length} foto nuk u ngarkuan dot: {failedPhotos.map(f => f.originalName).join(', ')}.
                  {' '}Këto skedarë erdhën bosh nga telefoni juaj - shpesh ndodh me foto iCloud të pashkarkuara plotësisht,
                  {' '}ose kur e hapni këtë faqe brenda Instagram/Facebook në vend të Safari/Chrome.
                  {' '}Ju lutem provoni t'i shtoni këto foto veçmas, ose na kontaktoni për ndihmë.
                </p>
              </div>
            )}

            <div className="manual-success-actions">
              <a
                href={orderPhotoUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="whatsapp-fallback-btn"
              >
                📸 Shiko Fotot
              </a>

              <button
                type="button"
                onClick={handleCopyPhotoLink}
                className="whatsapp-fallback-btn"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                {linkCopied ? '✅ Linku u Kopjua!' : '🔗 Kopjo Linkun'}
              </button>
            </div>

            <button
              onClick={() => navigate("/")}
              className="back-home-btn"
            >
              Kthehu në Faqen Kryesore
            </button>
          </div>
        </div>
      )}

      <main className="up-page">
        <div className="up-card">
          <h1 className="up-title">{uploadProduct.name}</h1>

          {inAppBrowser && (
            <div className="up-warning">
              ⚠️ Po e hapni këtë faqe brenda {inAppBrowser}. Për të shmangur probleme me ngarkimin e fotove,
              {' '}hapeni në Safari ose Chrome: prekni <strong>⋮</strong> ose <strong>•••</strong> lart djathtas
              {' '}dhe zgjidhni <strong>"Hap në Browser"</strong> ("Open in Browser" / "Open in Safari").
            </div>
          )}

          <p className="up-description">{uploadProduct.fullDescription}</p>

          <div className="up-options" role="radiogroup" aria-label="Zgjidhni Opsionin">
            {priceOptions.map((option) => {
              const active = selectedOption.label === option.label;
              return (
                <button
                  key={option.label}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  className={`up-option ${active ? 'is-active' : ''}`}
                  onClick={() => setSelectedOption(option)}
                >
                  <span className="up-option-label">{option.label}</span>
                  <span className="up-option-price">€{option.price.toFixed(2)}</span>
                </button>
              );
            })}
          </div>

          <label className="up-select-btn">
            <input
              type="file"
              multiple
              accept="image/*"
              onChange={handleImageUpload}
              style={{ display: 'none' }}
            />
            Zgjidh Foto
          </label>

          <p className="up-count">
            <strong>{uploadedImages.length}</strong> / {selectedOption.maxPhotos} foto
          </p>

          {uploadedImages.length > 0 && (
            <div className="up-grid">
              {uploadedImages.map((img, index) => (
                <div key={index} className="up-thumb">
                  <span className="up-thumb-num">{index + 1}</span>
                  {img.file && (img.file.type === 'image/heic' || img.name.toLowerCase().endsWith('.heic')) ? (
                    <div className="up-thumb-heic">
                      <a href={img.url} download={img.name}>Shkarko foton</a>
                    </div>
                  ) : (
                    <img src={img.url} alt={`Foto ${index + 1}`} />
                  )}
                  <button
                    type="button"
                    className="up-thumb-remove"
                    onClick={() => removeImage(index)}
                    aria-label={`Hiq foton ${index + 1}`}
                  >
                    ✕
                  </button>
                </div>
              ))}
            </div>
          )}

          <button
            type="button"
            className="up-generate-btn"
            onClick={() => setShowNameModal(true)}
            disabled={uploadedImages.length < selectedOption.minPhotos || uploading}
          >
            {uploading ? 'Duke gjeneruar linkun... Ju lutem prisni' : `Gjenero Linkun e Fotove - €${selectedOption.price.toFixed(2)}`}
          </button>
        </div>
      </main>
    </div>
  );
};

export default UploadProduct;
